import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { db } from "@lib/firebase";
import { useAuth } from "@context/AuthContext";
import { useNotification } from "@jotrseatrc/shared-components";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";

const cachePesananData = (uid, data) => {
  try {
    localStorage.setItem(
      `pesanan_${uid}`,
      JSON.stringify({ data, timestamp: Date.now() })
    );
  } catch (err) {
    console.warn("[CACHE] Gagal menyimpan cache:", err);
  }
};

const getCachedPesananData = (uid) => {
  try {
    const cached = localStorage.getItem(`pesanan_${uid}`);
    if (!cached) return null;
    const { data, timestamp } = JSON.parse(cached);
    const cacheDuration = 5 * 60 * 1000; // 5 menit
    if (Date.now() - timestamp > cacheDuration) {
      localStorage.removeItem(`pesanan_${uid}`);
      return null;
    }
    if (!data || !Array.isArray(data.pesanan)) {
      console.warn("[CACHE] Cache tidak valid: pesanan bukan array");
      localStorage.removeItem(`pesanan_${uid}`);
      return null;
    }
    return data;
  } catch (err) {
    console.warn("[CACHE] Gagal mengambil cache:", err);
    localStorage.removeItem(`pesanan_${uid}`);
    return null;
  }
};

const shortenLocation = (location) => {
  if (!location || typeof location !== "string") return "-";
  const parts = location.split(" ");
  if (parts.length === 1) return location.slice(0, 8);
  if (parts[0].toLowerCase().startsWith("jakarta")) {
    return `Jkt ${parts[1]}`;
  }
  return parts[0].slice(0, 4);
};

export const MyRegistration = React.memo(() => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [pesanan, setPesanan] = useState(() => {
    const cached = getCachedPesananData(user?.uid);
    return cached ? cached.pesanan : [];
  });
  const [suratPernyataan, setSuratPernyataan] = useState(() => {
    const cached = getCachedPesananData(user?.uid);
    return cached ? cached.suratPernyataan : {};
  });
  const [eventTitles, setEventTitles] = useState(() => {
    const cached = getCachedPesananData(user?.uid);
    return cached ? cached.eventTitles : {};
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const isMounted = useRef(true);
  const fetchStatus = useRef({ isFetching: false });

  const fetchPesananDanSurat = useCallback(
    async (forceRefresh = false) => {
      if (!user?.uid || fetchStatus.current.isFetching) {
        if (!user?.uid) setError("User not authenticated.");
        setLoading(false);
        return;
      }

      fetchStatus.current.isFetching = true;
      try {
        setLoading(true);
        setError("");

        if (!forceRefresh) {
          const cachedData = getCachedPesananData(user.uid);
          if (cachedData && isMounted.current) {
            setPesanan(cachedData.pesanan);
            setSuratPernyataan(cachedData.suratPernyataan);
            setEventTitles(cachedData.eventTitles || {});
            setLoading(false);
            return;
          }
        }

        const pesananList = [];
        const processedOrderIds = new Set();
        const suratData = {};
        const eventTitlesData = {};

        // Ambil judul event dari semuaKegiatan
        try {
          const eventsSnapshot = await getDocs(collection(db, "semuaKegiatan"));
          for (const eventDoc of eventsSnapshot.docs) {
            const eventId = eventDoc.id;
            const eventData = eventDoc.data();
            eventTitlesData[eventId] = eventData.title || eventId;
          }
          eventTitlesData["JOTR"] = "Jakarta Open Trail Run";
        } catch (err) {
          console.error("[FETCH] Gagal mengambil data semuaKegiatan:", err);
        }

        // Ambil pesanan event
        try {
          const eventsSnapshot = await getDocs(collection(db, "semuaKegiatan"));
          for (const eventDoc of eventsSnapshot.docs) {
            const eventId = eventDoc.id;
            const eventData = eventDoc.data();
            const collectionName =
              eventData.collectionName || `pendaftaran${eventId}`;
            // NOTE: We fetch pending/settlement but treat all as "success" in UI
            const pesananQuery = query(
              collection(db, collectionName),
              where("uid", "==", user.uid)
              // We could remove transaction_status filter to show everything, but sticking to valid ones is safer.
              // Assuming "no payment" means they are created but maybe pending.
              // Let's keep fetching pending/settlement.
            );
            const pesananSnapshot = await getDocs(pesananQuery);
            pesananSnapshot.forEach((doc) => {
              const data = doc.data();
              if (!processedOrderIds.has(doc.id)) {
                pesananList.push({
                  id: doc.id,
                  eventId,
                  collectionName,
                  kategori: data.kategori || null,
                  type: "event",
                  ...data,
                });
                processedOrderIds.add(doc.id);
              }
            });
          }
        } catch (err) {
          console.error("[FETCH] Gagal mengambil data pesanan event:", err);
        }

        // Ambil data surat pernyataan
        try {
          const suratQuery = query(
            collection(db, "suratPernyataan"),
            where("userId", "==", user.uid)
          );
          const suratSnapshot = await getDocs(suratQuery);
          suratSnapshot.forEach((doc) => {
            const data = doc.data();
            const eventId = data.eventId;
            if (eventId && data.userId === user.uid) {
              const existing = suratData[eventId];
              if (
                !existing ||
                (existing.createdAt &&
                  data.createdAt &&
                  data.createdAt.seconds > existing.createdAt.seconds)
              ) {
                suratData[eventId] = {
                  downloadURL: data.downloadURL,
                  fileName: data.fileName,
                  namaLengkap: data.namaLengkap,
                  createdAt: data.createdAt,
                };
              }
            }
          });
        } catch (err) {
          console.error("[FETCH] Gagal mengambil data suratPernyataan:", err);
        }

        if (isMounted.current) {
          setPesanan(pesananList);
          setSuratPernyataan(suratData);
          setEventTitles(eventTitlesData);
          cachePesananData(user.uid, {
            pesanan: pesananList,
            suratPernyataan: suratData,
            eventTitles: eventTitlesData,
          });
        }
      } catch (err) {
        if (isMounted.current) {
          setError("Failed to load order data. Please try again.");
          showNotification("error", "Failed to load order data.");
        }
      } finally {
        if (isMounted.current) setLoading(false);
        fetchStatus.current.isFetching = false;
      }
    },
    [user, showNotification]
  );

  useEffect(() => {
    isMounted.current = true;
    if (!user) {
      navigate("/login");
      showNotification("error", "Please login to view orders.");
      return;
    }

    fetchPesananDanSurat(true);

    return () => {
      isMounted.current = false;
    };
  }, [user, navigate, showNotification, fetchPesananDanSurat]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const getEventName = (item) => {
    if (item.type === "shuttle") {
      return `${
        eventTitles[item.eventId] || item.eventId || "Unknown"
      } - Shuttle`;
    }
    return eventTitles[item.eventId] || item.eventId || "Unknown";
  };

  return (
    <section className="min-h-screen bg-white px-4 py-20">
      <div className="max-w-6xl mx-auto">
        <div className="w-full">
            <div className="bg-white rounded-xl border border-slate-200 py-4 px-0">
              <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-4 gap-2 px-4">
                <h2 className="text-lg md:text-xl font-semibold text-slate-800">
                  Registration History
                </h2>
              </div>
              <AnimatePresence>
                {error && (
                  <div className="bg-red-100 text-red-800 p-4 rounded-xl mx-4 mb-4">
                    <FontAwesomeIcon
                      icon={faExclamationTriangle}
                      className="h-5 w-5 mr-2"
                    />
                    <span>{error}</span>
                  </div>
                )}
              </AnimatePresence>
              {loading ? (
                <p className="text-gray-600 my-4 mx-4">Loading data...</p>
              ) : pesanan.length === 0 ? (
                <p className="text-gray-600 my-4 mx-4">
                  No registrations yet.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full table-auto divide-y divide-gray-200">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Name
                        </th>
                        <th className="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                          Detail
                        </th>
                        <th className="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Declaration Letter
                        </th>
                        <th className="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {pesanan.map((item) => (
                        <tr
                          key={`${item.eventId}-${item.id}`}
                          className="border-b border-gray-200"
                        >
                          <td className="px-4 py-4 text-sm text-gray-600">
                            {getEventName(item)}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-600 truncate">
                            {item.type === "shuttle"
                              ? `${shortenLocation(
                                  item.pickupPoint
                                )} - ${shortenLocation(item.returnPoint)}`
                              : item.kategori || "-"}
                          </td>
                          <td className="px-4 py-4 text-sm">
                            <span
                              className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800"
                            >
                              Success
                            </span>
                          </td>
                          <td className="px-4 py-4 text-sm">
                            {item.type === "event" &&
                            item.kategori &&
                            suratPernyataan[item.eventId] ? (
                              <a
                                href={
                                  suratPernyataan[item.eventId].downloadURL
                                }
                                download={
                                  suratPernyataan[item.eventId].fileName
                                }
                                className="text-red-600 hover:text-red-900"
                              >
                                Download
                              </a>
                            ) : (
                              <span className="text-gray-500">-</span>
                            )}
                          </td>
                          <td className="px-4 py-4 text-sm">
                            <button
                              onClick={() =>
                                navigate(
                                  `/RegistrationDetail?eventId=${item.eventId}&orderId=${item.id}`
                                )
                              }
                              className="px-4 py-2 rounded-xl text-sm font-medium bg-red-600 text-white hover:bg-red-700 transition-colors duration-200 whitespace-nowrap"
                            >
                              View Details
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
        </div>
      </div>
    </section>
  );
});